import type { AgentDto } from '@/api/control/missionImpactAPI.schemas'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNow } from '@/hooks/useNow'
import {
  getHealthBadgeVariant,
  getHealthCheckStage,
  getHealthCheckStageVariant,
  isHealthCheckRunning,
  type HealthCheckStage,
} from '@/lib/agentHealth'

interface AgentHealthStatusProps {
  agent: AgentDto
}

/**
 * Shows an agent's health (Online, Checking, Offline) and, below it, the progress or result of its
 * latest health check. The check's stage advances every second while it waits for the agent.
 */
export function AgentHealthStatus({ agent }: AgentHealthStatusProps) {
  const { t } = useLanguage()
  const runningAtRender = isHealthCheckRunning(getHealthCheckStage(agent, Date.now()))
  const now = useNow(1_000, runningAtRender)
  const stage = getHealthCheckStage(agent, now)

  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant={getHealthBadgeVariant(agent.healthState)}>
        {t.agents.health[agent.healthState]}
      </Badge>
      {agent.healthState === 'Offline' && agent.offlineSinceUtc && (
        <span className="text-xs text-muted-foreground">
          {t.agents.health.offlineSince.replace('{time}', new Date(agent.offlineSinceUtc).toLocaleString())}
        </span>
      )}
      {stage !== 'idle' && (
        <HealthCheckStageBadge stage={stage} detail={agent.lastHealthCheckDetail} />
      )}
    </div>
  )
}

interface HealthCheckStageBadgeProps {
  stage: HealthCheckStage
  detail?: string | null
}

/**
 * Shows the stage of a health check, with the server's detail (e.g. which targets failed) on hover.
 */
function HealthCheckStageBadge({ stage, detail }: HealthCheckStageBadgeProps) {
  const { t } = useLanguage()
  const label = t.agents.healthCheck[stage === 'idle' ? 'checking' : stage]
  const badge = <Badge variant={getHealthCheckStageVariant(stage)}>{label}</Badge>

  if (!detail || isHealthCheckRunning(stage)) {
    return badge
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="max-w-xs">{detail}</TooltipContent>
    </Tooltip>
  )
}
